import { useEffect } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';

import { useLingui } from '@lingui/react/macro';

import { fmtNumber } from '../../i18n/format.js';
import { msg } from '@lingui/core/macro';
import { i18n } from '../../i18n/runtime.js';

/**
 * DrawerNavRail — cụm nút ▲▼ bám mép TRÁI popup, để duyệt bản ghi trước/sau mà không
 * phải đóng popup quay về danh sách.
 *
 * Chỗ đặt đổi theo bề rộng màn: từ `xl` trở lên thì nổi HẲN ra ngoài popup (nằm trên nền
 * mờ, không che nội dung); hẹp hơn thì popup đã ăn gần hết màn nên phải nép vào trong.
 *
 * `noun` đổi tên gọi theo ngữ cảnh ("Lead"/"Liên hệ"/"Công ty" cho CRM, "Đơn" cho Phê duyệt)
 * — cùng con số mà gọi đúng tên thì đỡ phải đoán "2 / 10.962" là gì.
 *
 * `keyHint` chỉ là CHỮ trong tooltip, KHÔNG tự gắn phím — mặc định khớp với useDrawerNavKeys()
 * (↑/↓ trần), là phím MỌI popup trong hệ đều dùng. Chỉ truyền prop này nếu consumer cố tình bind
 * phím khác; bind một đằng ghi một nẻo là tooltip nói dối.
 */
export default function DrawerNavRail({
  info,
  onGo,
  noun = i18n._(msg`Bản ghi`),
  keyHint = { prev: '↑', next: '↓' },
}) {
  const {
    t
  } = useLingui();

  if (!info) return null;
  const { position, total, hasPrev, hasNext } = info;

  // 10962 → 10.962: chuỗi số trần quá dài, đọc bằng mắt không ra ngay là bao nhiêu.
  const fmt = (n) => fmtNumber(Number(n));
  const label = t`${noun} thứ ${fmt(position)} trong ${fmt(total)}`;

  const btn = 'p-1.5 rounded-lg text-gray-500 transition enabled:hover:bg-gray-100 enabled:hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed';

  return (
    <div
      className="absolute top-1/2 -translate-y-1/2 z-20 flex flex-col items-center gap-0.5 rounded-2xl bg-white/95 backdrop-blur-sm shadow-lg ring-1 ring-black/5 p-1
                 left-2 xl:left-auto xl:right-full xl:mr-3"
    >
      <button type="button" onClick={() => onGo?.(-1)} disabled={!hasPrev}
        className={btn} title={t`${noun} trước (${keyHint.prev})`} aria-label={t`${noun} trước`}>
        <ChevronUp size={18} />
      </button>

      <div className="px-1 py-0.5 text-center leading-tight select-none" title={label} aria-label={label}>
        <div className="text-[11px] font-semibold text-gray-700 tabular-nums">{fmt(position)}</div>
        <div className="text-[9px] text-gray-400 tabular-nums">/{fmt(total)}</div>
      </div>

      <button type="button" onClick={() => onGo?.(1)} disabled={!hasNext}
        className={btn} title={`${noun} sau (${keyHint.next})`} aria-label={`${noun} sau`}>
        <ChevronDown size={18} />
      </button>
    </div>
  );
}

// Ô đang gõ / widget tự ăn phím mũi tên (dropdown, listbox, lịch...) — nhường phím cho chúng.
const TYPING = 'input, textarea, select, [contenteditable=""], [contenteditable="true"]';
const ARROW_WIDGET = '[role="listbox"], [role="combobox"], [role="menu"], [role="menuitem"], [role="option"], [role="tree"], [role="grid"], [role="spinbutton"]';

/**
 * useDrawerNavKeys — ↑/↓ trần để nhảy bản ghi trước/sau (Alt + ↑/↓ vẫn chạy, giữ thói quen cũ).
 *
 * Dùng phím trần là yêu cầu của sếp Phú (04.08.2026) — duyệt lead phải một tay. Đánh đổi:
 * mất cuộn body popup bằng phím mũi tên (chuột/PageUp/PageDown/Space vẫn cuộn bình thường).
 *
 * `e.repeat` bị bỏ qua CÓ CHỦ Ý: giữ phím sẽ bắn liên tiếp, mỗi bước là 2 request chi tiết
 * → phải gõ từng nhịp.
 */
export function useDrawerNavKeys(onGo, enabled = true) {
  useEffect(() => {
    if (!enabled || !onGo) return;
    const handler = (e) => {
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
      if (e.ctrlKey || e.metaKey || e.shiftKey) return;
      if (e.repeat) return;
      const el = e.target;
      if (el?.isContentEditable) return;
      if (el?.closest?.(TYPING) || el?.closest?.(ARROW_WIDGET)) return;
      e.preventDefault();
      onGo(e.key === 'ArrowUp' ? -1 : 1);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onGo, enabled]);
}
